import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import useScopedTranslation from 'hooks/useScopedTranslation';
import {
  getCaseOutbound,
  getOutboundLog,
} from 'services/escriturasOutboundService';
import { useSnackbar } from 'context/SnackbarContext';
import ResendOutboundModal from 'views/Clients/ClientsDetail/tabs/EEPPBeta/ResendOutboundModal';
import { escriturasOutboundI18n } from './i18n';
import { formatDateTime } from './format';
import '../../styles/naEeppTableGrid.scss';

// Valores del filtro de estado. 'problematic' es el default (el backend lo
// traduce a failed,bounced,queued); 'all' trae todo; el resto son puntuales.
const STATUS_FILTER_VALUES = [
  'problematic',
  'all',
  'sent',
  'failed',
  'bounced',
  'queued',
];

const CHANNEL_FILTER_VALUES = ['all', 'email', 'whatsapp'];

// Estados sobre los que se ofrece reenviar.
const RESENDABLE = new Set(['failed', 'bounced']);

// Cuántas filas pedir al server (paginamos client-side sobre este lote).
const FETCH_TAKE = 500;

/**
 * Vista global "Log de Correos" (submenú EEPP) — espejo de la vista de triage.
 *
 * Lista los mensajes salientes (email/WhatsApp) del módulo EEPP con su estado,
 * tipo (label humano) y destinatario. Filtros de estado (default Problemáticos)
 * y canal; paginación client-side. Las filas fallidas/rebotadas muestran su
 * motivo y un botón Reenviar que abre el modal compartido ResendOutboundModal.
 *
 * Modo caso: si la URL trae `?case={id}` (entero válido), la vista trae solo los
 * salientes de ese caso vía getCaseOutbound (sin filtros de estado/canal) y
 * muestra un header con la opción "Ver todos" para volver al log global.
 */
export default function EscriturasOutboundLog({ DesignSystemComponents }) {
  const { t } = useScopedTranslation(escriturasOutboundI18n);
  const { showSnackbar } = useSnackbar();

  const [searchParams, setSearchParams] = useSearchParams();
  const caseParam = searchParams.get('case');
  const parsedCaseId = Number.parseInt(caseParam ?? '', 10);
  const caseId =
    Number.isInteger(parsedCaseId) && parsedCaseId > 0 ? parsedCaseId : null;

  const { Button, Table, Badge, IconButton, Dropdown, Card, Skeleton } =
    DesignSystemComponents ?? {};

  const [statusFilter, setStatusFilter] = useState('problematic');
  const [channelFilter, setChannelFilter] = useState('all');
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(20);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [resendMessage, setResendMessage] = useState(null);

  const abortRef = useRef(null);

  const fetchList = useCallback(async () => {
    if (abortRef.current) abortRef.current.abort();
    const controller = new AbortController();
    abortRef.current = controller;

    setLoading(true);
    setError(null);
    try {
      const data = caseId
        ? await getCaseOutbound(caseId, controller.signal)
        : await getOutboundLog(
            {
              status: statusFilter === 'problematic' ? undefined : statusFilter,
              channel: channelFilter === 'all' ? undefined : channelFilter,
              take: FETCH_TAKE,
              skip: 0,
            },
            controller.signal
          );
      setRows(Array.isArray(data) ? data : []);
    } catch (err) {
      if (err.name === 'AbortError' || err.name === 'CanceledError') return;
      setError(
        err.response?.data?.message ||
          err.message ||
          t('cd_outbound_error_generic')
      );
    } finally {
      setLoading(false);
    }
  }, [caseId, statusFilter, channelFilter, t]);

  useEffect(() => {
    fetchList();
    return () => abortRef.current?.abort();
  }, [fetchList]);

  // Vuelve a la primera página al cambiar de alcance (entrar/salir de modo caso),
  // así la paginación client-side no queda en una página inexistente del set nuevo.
  useEffect(() => {
    setPage(0);
  }, [caseId]);

  const statusOptions = useMemo(
    () =>
      STATUS_FILTER_VALUES.map((value) => ({
        label: t(`cd_outbound_status_${value}`),
        value,
      })),
    [t]
  );
  const selectedStatusOption =
    statusOptions.find((o) => o.value === statusFilter) || statusOptions[0];

  const channelOptions = useMemo(
    () =>
      CHANNEL_FILTER_VALUES.map((value) => ({
        label: t(`cd_outbound_channel_${value}`),
        value,
      })),
    [t]
  );
  const selectedChannelOption =
    channelOptions.find((o) => o.value === channelFilter) || channelOptions[0];

  const statusBadge = useCallback(
    (status) => {
      const key = (status || '').toLowerCase();
      const color =
        key === 'sent'
          ? 'success'
          : key === 'failed' || key === 'bounced'
            ? 'error'
            : key === 'queued'
              ? 'warning'
              : 'default';
      const label = t(`cd_outbound_status_${key}`) || status || '—';
      return Badge ? (
        <Badge text={label} color={color} size='sm' />
      ) : (
        <span>{label}</span>
      );
    },
    [Badge, t]
  );

  const handleResent = useCallback(() => {
    showSnackbar?.(t('cd_outbound_resend_success'), 'success');
    setResendMessage(null);
    fetchList();
  }, [showSnackbar, t, fetchList]);

  // Mapa id → fila del lote actual, para resolver el estado del reintento
  // (resentMessageId) sin un fetch extra: si el nuevo saliente está en la lista
  // cargada mostramos su estado inline ("→ Enviado"/"→ Falló").
  const rowsById = useMemo(() => {
    const map = new Map();
    for (const r of rows) map.set(r.id, r);
    return map;
  }, [rows]);

  const columns = useMemo(
    () => [
      {
        accessor: 'sentAt',
        label: t('cd_outbound_col_date'),
        render: (row) => formatDateTime(row.sentAt),
      },
      {
        accessor: 'channel',
        label: t('cd_outbound_col_channel'),
        render: (row) =>
          t(`cd_outbound_channel_${(row.channel || '').toLowerCase()}`) ||
          row.channel ||
          '—',
      },
      {
        accessor: 'templateLabel',
        label: t('cd_outbound_col_type'),
        render: (row) => row.templateLabel || row.templateKey || '—',
      },
      {
        accessor: 'toIdentifier',
        label: t('cd_outbound_col_recipient'),
        render: (row) => row.toIdentifier || '—',
      },
      {
        accessor: 'caseId',
        label: t('cd_outbound_col_case'),
        render: (row) =>
          row.caseId ? (
            <Link
              to={`/detalle-causa/${row.caseId}`}
              className='text-primary text-decoration-none'
            >
              {row.caseId}
            </Link>
          ) : (
            '—'
          ),
      },
      {
        accessor: 'status',
        label: t('cd_outbound_col_status'),
        // align-items-start: el Badge (size='sm') queda inline y NO se estira a
        // todo el ancho de la celda (el flex-column por defecto estira al cross-axis).
        render: (row) => {
          const reason = row.bounceReason || row.errorMessage;
          const resentRow = row.resentMessageId
            ? rowsById.get(row.resentMessageId)
            : null;
          return (
            <div
              className='d-flex flex-column align-items-start'
              style={{ gap: 2 }}
            >
              {statusBadge(row.status)}
              {/* (R7) Marca de reenvío: si la falla/rebote ya fue reintentada,
                  mostramos cuándo y, si el nuevo saliente está en el lote, su estado. */}
              {row.resentAt && (
                <span
                  className='mini'
                  style={{ color: '#64748B', whiteSpace: 'normal' }}
                >
                  {t('cd_outbound_resent_at', {
                    date: formatDateTime(row.resentAt),
                  })}
                  {resentRow &&
                    ` → ${t(`cd_outbound_status_${(resentRow.status || '').toLowerCase()}`) || resentRow.status}`}
                </span>
              )}
              {reason && (
                <span
                  className='mini'
                  style={{
                    color: '#B45309',
                    maxWidth: 260,
                    whiteSpace: 'normal',
                  }}
                  title={reason}
                >
                  {t('cd_outbound_reason_label')}: {reason}
                </span>
              )}
            </div>
          );
        },
      },
      {
        label: t('cd_outbound_col_actions'),
        // failed/bounced ofrecen Reenviar SOLO si aún no fueron reenviados; una vez
        // reenviada (resentAt) la fila ya fue gestionada → se oculta el botón.
        render: (row) =>
          RESENDABLE.has((row.status || '').toLowerCase()) && !row.resentAt ? (
            IconButton ? (
              <IconButton
                ariaLabel={t('cd_outbound_action_resend')}
                icon='arrowCounterclockwise'
                size='sm'
                stroke='var(--primary-500)'
                onClick={() => setResendMessage(row)}
              />
            ) : (
              <button
                type='button'
                className='btn btn-link btn-sm'
                onClick={() => setResendMessage(row)}
              >
                {t('cd_outbound_action_resend')}
              </button>
            )
          ) : null,
      },
    ],
    [t, statusBadge, rowsById, IconButton]
  );

  const ContentWrapper = Card || 'div';
  const contentWrapperProps = Card
    ? { specialPadding: 'p-4' }
    : { className: 'container-fluid p-4' };

  return (
    <div>
      <div className='mb-4 d-flex justify-content-between align-items-start'>
        <div>
          <h2 className='fw-semibold'>{t('cd_outbound_title')}</h2>
          <h4 className='fw-normal'>{t('cd_outbound_subtitle')}</h4>
        </div>
        {IconButton && (
          <IconButton
            ariaLabel={t('cd_outbound_refresh')}
            icon='arrowCounterclockwise'
            size='lg'
            iconFill='var(--primary-500)'
            onClick={fetchList}
            disabled={loading}
          />
        )}
      </div>

      <ContentWrapper {...contentWrapperProps}>
        {error && (
          <div className='alert alert-danger py-2' style={{ fontSize: 13 }}>
            {error}
          </div>
        )}

        {/* Modo caso: header con el alcance + "Ver todos" (limpia el param y
          vuelve al log global con sus filtros). */}
        {caseId ? (
          <div className='d-flex align-items-center gap-2 mb-3'>
            <span className='caption font-light mb-0'>
              {t('cd_outbound_case_scope', { caseId })}
            </span>
            {Button ? (
              <Button
                label={t('cd_outbound_case_clear')}
                size='sm'
                variant='naked'
                onClick={() => {
                  setSearchParams({});
                  setPage(0);
                }}
              />
            ) : (
              <button
                type='button'
                className='btn btn-link btn-sm p-0'
                onClick={() => {
                  setSearchParams({});
                  setPage(0);
                }}
              >
                {t('cd_outbound_case_clear')}
              </button>
            )}
          </div>
        ) : (
          <div className='d-flex flex-wrap gap-2 mb-3'>
            <div style={{ flex: '0 1 220px' }}>
              {Dropdown ? (
                <Dropdown
                  options={statusOptions}
                  value={selectedStatusOption}
                  onChange={(option) => {
                    setStatusFilter(option?.value || 'problematic');
                    setPage(0);
                  }}
                  placeholder={t('cd_outbound_filter_status')}
                  width='100%'
                  isDisabled={loading}
                />
              ) : (
                <select
                  className='form-select form-select-sm'
                  value={statusFilter}
                  onChange={(e) => {
                    setStatusFilter(e.target.value);
                    setPage(0);
                  }}
                >
                  {statusOptions.map((o) => (
                    <option key={o.value} value={o.value}>
                      {o.label}
                    </option>
                  ))}
                </select>
              )}
            </div>
            <div style={{ flex: '0 1 200px' }}>
              {Dropdown ? (
                <Dropdown
                  options={channelOptions}
                  value={selectedChannelOption}
                  onChange={(option) => {
                    setChannelFilter(option?.value || 'all');
                    setPage(0);
                  }}
                  placeholder={t('cd_outbound_filter_channel')}
                  width='100%'
                  isDisabled={loading}
                />
              ) : (
                <select
                  className='form-select form-select-sm'
                  value={channelFilter}
                  onChange={(e) => {
                    setChannelFilter(e.target.value);
                    setPage(0);
                  }}
                >
                  {channelOptions.map((o) => (
                    <option key={o.value} value={o.value}>
                      {o.label}
                    </option>
                  ))}
                </select>
              )}
            </div>
          </div>
        )}

        {loading && rows.length === 0 ? (
          <div className='d-flex flex-column gap-2'>
            {Skeleton ? (
              [0, 1, 2, 3, 4].map((i) => (
                <Skeleton key={i} height='2.5rem' width='100%' />
              ))
            ) : (
              <p className='font-light'>{t('cd_outbound_loading')}</p>
            )}
          </div>
        ) : (
          <div className='table-container na-eepp-grid-7'>
            {Table ? (
              <Table
                columns={columns}
                data={rows}
                loading={loading}
                paginator
                paginationMode='client'
                page={page}
                onPageChange={setPage}
                pageSize={pageSize}
                onPageSizeChange={(newSize) => {
                  setPageSize(newSize);
                  setPage(0);
                }}
                totalItems={rows.length}
                paginatorGroup={[
                  { label: '10', value: 10 },
                  { label: '20', value: 20 },
                  { label: '50', value: 50 },
                ]}
              />
            ) : (
              <p className='font-light'>
                {loading ? t('cd_outbound_loading') : null}
              </p>
            )}
            {!loading && rows.length === 0 && (
              <div className='text-center p-4'>
                <p className='caption font-light'>
                  {t('cd_outbound_no_results')}
                </p>
              </div>
            )}
          </div>
        )}

        <ResendOutboundModal
          DesignSystemComponents={DesignSystemComponents}
          show={Boolean(resendMessage)}
          message={resendMessage}
          onClose={() => setResendMessage(null)}
          onResent={handleResent}
        />
      </ContentWrapper>
    </div>
  );
}
